import type { ExpenseResponseDto } from '@splitfiesta/shared';

/** Group membership role as far as expense permissions are concerned. */
export type ExpenseViewerRole = 'owner' | 'admin' | 'member';

/**
 * The single rule for "may this viewer edit or delete this expense?",
 * mirroring what the API enforces: the creator always may, and a group
 * admin or owner may touch anyone's expense.
 *
 * The role itself is NOT resolved here: mobile reads it from
 * `group.balances[].role`, web from `group.userRole` or its members list.
 * Each caller keeps its own lookup and passes the answer in, so the
 * decision lives in one place without core knowing either shape.
 */
export const ExpensePermissionService = {
  canModifyExpense(
    expense: Pick<ExpenseResponseDto, 'createdBy'> | null | undefined,
    viewerId: string | undefined,
    viewerRole: ExpenseViewerRole | undefined
  ): boolean {
    if (!expense || !viewerId) return false;
    if (expense.createdBy === viewerId) return true;
    return viewerRole === 'admin' || viewerRole === 'owner';
  },

  /** Who may nudge the table to pick: anyone who paid, or a group admin or owner. */
  canRemindPicks(
    expense: Pick<ExpenseResponseDto, 'payers'> | null | undefined,
    viewerId: string | undefined,
    viewerRole: ExpenseViewerRole | undefined
  ): boolean {
    if (!expense || !viewerId) return false;
    if (expense.payers.some(p => p.userId === viewerId)) return true;
    return viewerRole === 'admin' || viewerRole === 'owner';
  },
};
